import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectHasCapabilityPersonalTranscription,
    selectHasCapabilityBusinessTranscription,
    selectHasCapabilityLegacyTranscribeOnCall,
} from 'flow/selectors/own-extension-selectors';
import {
    selectCallRecordIsPersonalRecording,
    selectCallRecordIsTranscribable,
    selectCallRecordRecordedCallKey,
} from 'flow/selectors/call-records-selectors';
import { CallRecordKey } from 'flow/types/EntityKeys';
import ButtonGenerateExtendedTranscription from './ButtonGenerateExtendedTranscription';
import ButtonGenerateLegacyTranscription from './ButtonGenerateLegacyTranscription';

interface Props {
    callRecordKey: CallRecordKey;
    transcriptionIsPending?: boolean;
    onAwaitTranscriptionConfirmation?: () => void;
    isGeneratingLegacyTranscription?: boolean;
    onLegacyTranscribe?: () => Promise<unknown>;
    onClickGenerate?: () => void;
}

const ButtonGenerateTranscription = ({
    callRecordKey,
    transcriptionIsPending,
    onAwaitTranscriptionConfirmation,
    isGeneratingLegacyTranscription,
    onLegacyTranscribe,
    onClickGenerate,
}: Props) => {
    const hasPersonalCapability = useAppSelector(
        selectHasCapabilityPersonalTranscription,
    );
    const hasBusinessCapability = useAppSelector(
        selectHasCapabilityBusinessTranscription,
    );
    const hasLegacyCapability = useAppSelector(
        selectHasCapabilityLegacyTranscribeOnCall,
    );
    const recordedCallKey = useAppSelector((state) =>
        selectCallRecordRecordedCallKey(state, callRecordKey),
    );
    const isPersonalRecording = useAppSelector((state) =>
        selectCallRecordIsPersonalRecording(state, callRecordKey),
    );
    const isTranscribable = useAppSelector((state) =>
        selectCallRecordIsTranscribable(state, callRecordKey),
    );

    if (!recordedCallKey) return null;

    const hasRequiredExtendedTranscriptionCapability = isPersonalRecording
        ? hasPersonalCapability
        : hasBusinessCapability;

    if (hasRequiredExtendedTranscriptionCapability) {
        return (
            <ButtonGenerateExtendedTranscription
                callRecordKey={callRecordKey}
                transcriptionIsPending={transcriptionIsPending}
                onAwaitTranscriptionConfirmation={
                    onAwaitTranscriptionConfirmation
                }
                onClickGenerate={onClickGenerate}
            />
        );
    }

    if (hasLegacyCapability && isTranscribable) {
        return (
            <ButtonGenerateLegacyTranscription
                isGenerating={!!isGeneratingLegacyTranscription}
                onTranscribe={onLegacyTranscribe}
                onClickGenerate={onClickGenerate}
            />
        );
    }

    return null;
};

export default ButtonGenerateTranscription;
